/**
 * certs-issue-core.js — pure, alias-free core for the customer Issue view
 * (Unified Certificate Service design §6.7 "Customer exposure", A-17/A-18/
 * A-20; task 3.5f). The Vue modal and `services/cluster-api.js` are thin over
 * this module so `node --test` can exercise every load-bearing rule with a
 * fake `http` / `fetch`.
 *
 * Backend contract mirrored (cluster-api routes/certificates.py, §8.5):
 *   PUT  /cluster/certificates/registrations/ensure          (source: manual)
 *   GET  /cluster/certificates/registrations/<id>/progress   (SSE | JSON)
 *   POST /cluster/certificates/registrations/<id>/generate   (Retry — resumes)
 *   POST /cluster/certificates/registrations/<id>/upload     (BYO PEM, A-18)
 *   POST /cluster/certificates/registrations/<id>/validate   (step 3 on demand)
 *   GET  /cluster/certificates/registrations?org_id=
 *
 * All of them need the grantable `certificate.issue`; the grant is an explicit
 * auth-api row (`certificate` @ `org:<id>` carrying
 * `field_permissions.issue.enabled`) — `hasCertificateIssueGrant` is the
 * client-side twin of cluster-api's `helpers/certificate_issue_grants.py`.
 *
 * No `@/` imports — keep this module pure.
 */

import { isSuperAdminUser } from './environments-visibility.js'

const BASE = '/cluster/certificates/registrations'

/** The eight-step ladder the backend streams (§6.7 — 6a cleanup included). */
export const ISSUE_STEPS = ['ensure', 'dns', 'validate', 'order', 'challenge', 'issued', 'cleanup', 'done']

export const STEP_COPY = {
  ensure: { label: 'Register', hint: 'One registration per secret; a repeat click changes nothing.' },
  dns: { label: 'DNS records', hint: 'Private names get a permanent private record; a temporary public one exists only while the challenge runs.' },
  validate: { label: 'Validate DNS', hint: 'Read back from the provider and a public resolver — nothing is ordered until both agree.' },
  order: { label: 'Order', hint: 'cert-manager Certificate created.' },
  challenge: { label: 'Challenge', hint: 'The ACME challenge is answered.' },
  issued: { label: 'Issued', hint: 'Secret delivered to the environment.' },
  cleanup: { label: 'Clean up', hint: 'Temporary public records removed.' },
  done: { label: 'Done', hint: 'Delivered.' }
}

export const ISSUER_PROFILES = [
  { key: 'staging', label: 'Let’s Encrypt — Staging', hint: 'Untrusted test certificate. Every zone starts here; production follows the first successful issue.' },
  { key: 'production', label: 'Let’s Encrypt — Production', hint: 'Browser-trusted. Forced back to staging until the zone has issued once.' }
]

export const PRIVATE_NOTE =
  'A private name keeps a permanent private record; only a temporary public record exists during issuance and it is deleted afterwards.'

function enc(v) { return encodeURIComponent(String(v)) }
function str(v) { return String(v === null || v === undefined ? '' : v).trim() }

const DOMAIN_RE = /^(\*\.)?([a-z0-9](-?[a-z0-9])*\.)+[a-z]{2,}$/i
const CERT_RE = /-----BEGIN CERTIFICATE-----[\s\S]+-----END CERTIFICATE-----/
const KEY_RE = /-----BEGIN (?:RSA |EC |ENCRYPTED )?PRIVATE KEY-----[\s\S]+-----END (?:RSA |EC |ENCRYPTED )?PRIVATE KEY-----/

/** One hostname row: private ON by default (A-20). */
export function hostnameRow(name = '', isPrivate = true) {
  return { name: str(name).toLowerCase(), private: isPrivate !== false }
}

/**
 * Add a free-text name to the hostname rows. Refuses junk, duplicates and any
 * name the owner supplies themselves (a `byo-only` Domain is never issued).
 * A new name starts Private — the lane that can always complete.
 *
 *   → { hostnames, error }
 */
export function addHostnameRow(rows, name, { uploadOnly = [], isPrivate = true } = {}) {
  const list = Array.isArray(rows) ? rows : []
  const next = str(name).toLowerCase()
  if (!next) return { hostnames: list, error: 'Enter a hostname.' }
  if (!DOMAIN_RE.test(next)) return { hostnames: list, error: `"${next}" is not a hostname.` }
  if (list.some((h) => str(h && h.name).toLowerCase() === next)) {
    return { hostnames: list, error: `${next} is already on this certificate.` }
  }
  if ((uploadOnly || []).map((n) => str(n).toLowerCase()).includes(next)) {
    return { hostnames: list, error: `${next} is supplied by your organisation — upload its certificate instead.` }
  }
  return { hostnames: [...list, hostnameRow(next, isPrivate)], error: null }
}

/** Derived exposure of a hostname set, read-only (all private → internal, none → external, mixed → dual). */
export function exposureOf(hostnames) {
  const rows = (hostnames || []).filter((h) => str(h && h.name))
  if (!rows.length) return 'internal'
  const priv = rows.filter((h) => h.private !== false).length
  if (priv === rows.length) return 'internal'
  if (priv === 0) return 'external'
  return 'dual'
}

/** A safe default Secret name for a hostname (`app.example.com` → `app-example-com-tls`). */
export function defaultSecretName(hostname) {
  const base = str(hostname).toLowerCase().replace(/^\*\./, 'wildcard.').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  return base ? `${base}-tls`.slice(0, 63) : ''
}

/** The initial form for the modal. */
export function initialIssueForm({ defaultDomain = '', cluster = '', namespace = '' } = {}) {
  return {
    source: 'acme',
    hostnames: [hostnameRow(defaultDomain, true)],
    cluster: str(cluster),
    namespace: str(namespace),
    secretName: defaultSecretName(defaultDomain),
    issuerProfile: 'staging',
    tlsCrt: '',
    tlsKey: '',
    keepForReuse: false,
    replace: false
  }
}

/**
 * Validate the Issue form. Returns an errors map keyed by field; empty = valid.
 * cluster / namespace come from the environment and are not user input.
 */
export function validateIssueForm(form = {}) {
  const errors = {}
  const rows = (form.hostnames || []).filter((h) => str(h && h.name))
  if (!rows.length) errors.hostnames = 'Add at least one hostname.'
  else {
    const bad = rows.find((h) => !DOMAIN_RE.test(str(h.name)))
    if (bad) errors.hostnames = `"${str(bad.name)}" is not a hostname.`
    const seen = new Set()
    for (const h of rows) {
      const n = str(h.name).toLowerCase()
      if (seen.has(n)) { errors.hostnames = `"${n}" is listed twice.`; break }
      seen.add(n)
    }
  }
  if (!str(form.cluster)) errors.cluster = 'Environment cluster is unknown.'
  if (!str(form.namespace)) errors.namespace = 'Environment namespace is unknown.'
  const secret = str(form.secretName)
  if (!secret) errors.secretName = 'Secret name is required.'
  else if (!/^[a-z0-9]([-a-z0-9.]*[a-z0-9])?$/.test(secret) || secret.length > 253) {
    errors.secretName = 'Use lowercase letters, digits, dots and dashes.'
  }
  if (form.source === 'acme' && !['staging', 'production'].includes(str(form.issuerProfile))) {
    errors.issuerProfile = 'Choose an issuer.'
  }
  if (form.source === 'upload') {
    if (!str(form.tlsCrt)) errors.tlsCrt = 'Certificate (PEM) is required.'
    else if (!CERT_RE.test(form.tlsCrt)) errors.tlsCrt = 'Paste a PEM block beginning with -----BEGIN CERTIFICATE-----.'
    if (!str(form.tlsKey)) errors.tlsKey = 'Private key (PEM) is required.'
    else if (!KEY_RE.test(form.tlsKey)) errors.tlsKey = 'Paste a PEM private key block.'
  }
  return errors
}

/**
 * `PUT /registrations/ensure` body (§6.7 step 1). `owner.org_id` is the
 * caller's org — the backend pins it anyway for non-operators; validation is
 * `auto` for customers (no forced lane is ever sent).
 */
export function buildEnsureBody(form = {}, { orgId = null, wait = true, budgetS = 240 } = {}) {
  const mode = form.source === 'upload' ? 'byo' : 'acme'
  const body = {
    cluster: str(form.cluster),
    namespace: str(form.namespace),
    secret_name: str(form.secretName),
    hostnames: (form.hostnames || []).filter((h) => str(h && h.name)).map((h) => hostnameRow(h.name, h.private)),
    source: 'manual',
    certificate: mode === 'acme'
      ? { mode: 'acme', issuer_profile: str(form.issuerProfile) || 'staging' }
      : { mode: 'byo' },
    wait: mode === 'acme' ? !!wait : false,
    budget_s: budgetS,
    validate: true
  }
  if (orgId !== null && orgId !== undefined && orgId !== '') body.owner = { org_id: orgId }
  return body
}

/** `POST /registrations/<id>/upload` body (A-18). Customers own what they upload. */
export function buildIssueUploadBody(form = {}) {
  return {
    tls_crt: str(form.tlsCrt),
    tls_key: str(form.tlsKey),
    keep_for_reuse: !!form.keepForReuse,
    replace: !!form.replace,
    renewal_owner: 'customer'
  }
}

// ── envelope + errors ───────────────────────────────────────────────────────

/** Unwrap the unified envelope (same semantics as cluster-api.js `unwrap`). */
export function unwrapEnvelope(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

/**
 * The whole refusal, verbatim, for the UI: `{ status, code, message, errors[],
 * data }`. Recognises the unified envelope AND the bare cs-packages
 * `{error: "forbidden", resource, action}` shape.
 */
export function issueErrorDetails(err) {
  const resp = err && err.response
  const status = resp ? resp.status : undefined
  const body = resp && resp.data && typeof resp.data === 'object' ? resp.data : {}
  const errors = Array.isArray(body.errors)
    ? body.errors.map((e) => (typeof e === 'string' ? e : (e && (e.message || e.error)) || JSON.stringify(e)))
    : []
  let code = typeof body.error === 'string' ? body.error : null
  if (!code && body.data && typeof body.data === 'object' && typeof body.data.error === 'string') code = body.data.error
  let message = (typeof body.message === 'string' && body.message) || (err && err.message) || 'Request failed'
  if (status === 403 && !body.message) message = 'certificate.issue is not granted for your organisation.'
  if (status === 401) message = 'Your session expired. Please sign in again.'
  const data = body.data && typeof body.data === 'object' ? body.data : null
  return { status, code, message, errors, data }
}

/** True when an error is the permission gate (403), i.e. "no grant". */
export function isForbidden(err) {
  return !!(err && err.response && err.response.status === 403)
}

// ── the grant (client-side twin of helpers/certificate_issue_grants.py) ────

const LIVE_STATES = [null, undefined, '', 'active', 'approved']

function segments(path) {
  if (!path) return []
  return String(path).split('/').map((s) => s.trim()).filter((s) => s && !s.startsWith('datacenter:'))
}

/** Does one auth-api grant row name the `issue` action on `certificate`? */
export function grantCarriesIssue(row) {
  if (!row || String(row.resource_name || row.resource || '').toLowerCase() !== 'certificate') return false
  const fp = row.field_permissions
  if (!fp || typeof fp !== 'object') return false
  const entry = fp.issue
  if (entry && typeof entry === 'object') return !!(entry.enabled || entry.write)
  return entry === true
}

/**
 * The caller holds `certificate.issue` for `orgId` iff a live, unexpired
 * `certificate` row carrying `field_permissions.issue` is scoped to
 * `org:<orgId>` (or is unscoped). Fail closed on anything odd.
 */
export function hasCertificateIssueGrant(grants, orgId, now = Date.now()) {
  if (orgId === null || orgId === undefined || orgId === '' || !Array.isArray(grants)) return false
  const want = [`org:${String(orgId).trim()}`]
  return grants.some((row) => {
    if (!grantCarriesIssue(row)) return false
    const state = typeof row.state === 'string' ? row.state.trim().toLowerCase() : row.state
    if (!LIVE_STATES.includes(state)) return false
    if (row.expires_at) {
      const t = Date.parse(row.expires_at)
      if (isNaN(t) || t <= now) return false
    }
    const raw = row.scope_path
    if (raw === null || raw === undefined || String(raw).trim() === '') return true
    const segs = segments(raw)
    if (!segs.length) return false
    return segs.length <= want.length && want.slice(0, segs.length).every((s, i) => s === segs[i])
  })
}

// ── who may ask for a certificate ───────────────────────────────────────────

export const NO_GRANT_CUSTOMER_NOTE = 'Ask your organisation admin for certificate access.'
export const NO_GRANT_CUSTOMER_HINT = 'Org admins grant it from Members → Access.'

/**
 * May this caller ask the platform to issue a certificate?
 *
 * Two ways in, matching what cluster-api actually enforces on
 * `registrations/ensure`: an explicit `certificate.issue` grant row, OR the
 * permission plane's super-admin bypass — which leaves no grant row at all, so
 * `hasCertificateIssueGrant` alone answers false for an operator who can in
 * fact issue. That mismatch is what dropped super-admins onto the legacy
 * request path.
 *
 *   → { canIssue, via: 'super-admin'|'grant'|null, note, hint }
 */
export function certificateIssueAccess({ user = null, grants = null, orgId = null, now = Date.now() } = {}) {
  if (isSuperAdminUser(user)) return { canIssue: true, via: 'super-admin', note: null, hint: null }
  if (hasCertificateIssueGrant(grants, orgId, now)) return { canIssue: true, via: 'grant', note: null, hint: null }
  return { canIssue: false, via: null, note: NO_GRANT_CUSTOMER_NOTE, hint: NO_GRANT_CUSTOMER_HINT }
}

// ── 422 hostname_not_public ─────────────────────────────────────────────────

export const HOSTNAME_NOT_PUBLIC_CODE = 'hostname_not_public'

export const HOSTNAME_NOT_PUBLIC_HINT =
  'Switch these names to Private. The platform then publishes a temporary public record for the challenge and removes it once the certificate is issued.'

/**
 * The typed twin of a 422 `hostname_not_public` refusal: the name has no
 * public DNS, so a public-lane challenge can never complete. Accepts an
 * axios-style error, a bare response, or an `ensure()` result. Returns null
 * for anything else.
 */
export function hostnameNotPublicRefusal(errOrResult) {
  if (!errOrResult) return null
  const resp = errOrResult.response ||
    (errOrResult.data !== undefined && errOrResult.status !== undefined && errOrResult.ok === undefined ? errOrResult : null)
  const status = resp ? resp.status : errOrResult.status
  const body = resp
    ? (resp.data && typeof resp.data === 'object' ? resp.data : {})
    : { code: errOrResult.code, message: errOrResult.message, data: errOrResult.data, details: errOrResult.details }
  const data = body.data && typeof body.data === 'object' ? body.data : {}
  const det = (body.details && typeof body.details === 'object' && body.details) || {}
  const codes = [body.error_code, body.error, body.code, data.error_code, data.error, data.code, det.error_code, det.code]
  if (!codes.some((c) => c === HOSTNAME_NOT_PUBLIC_CODE)) return null
  if (status !== 422) return null
  const pick = (k) => (det[k] !== undefined ? det[k] : data[k] !== undefined ? data[k] : body[k])
  const raw = pick('hostnames')
  const hostnames = (Array.isArray(raw) ? raw : [])
    .map((h) => str(typeof h === 'string' ? h : h && h.name).toLowerCase())
    .filter(Boolean)
  return {
    code: HOSTNAME_NOT_PUBLIC_CODE,
    status,
    message: (typeof body.message === 'string' && body.message) ||
      'These hostnames have no public DNS, so a public challenge cannot complete.',
    hint: HOSTNAME_NOT_PUBLIC_HINT,
    hostnames
  }
}

// ── progress ladder ─────────────────────────────────────────────────────────

/** An empty ladder: every step pending, nothing terminal. */
export function initialProgress(registrationId = null) {
  return {
    registrationId,
    state: null,
    terminal: false,
    failedStep: null,
    lastError: null,
    steps: ISSUE_STEPS.map((step) => ({ step, status: 'pending', reason: null, at: null })),
    dns: null,
    certificate: null,
    transport: null,
    error: null,
    ended: false
  }
}

function mergeSteps(prev, incoming) {
  const byName = {}
  prev.forEach((s) => { byName[s.step] = { ...s } })
  ;(incoming || []).forEach((s) => {
    if (!s || !s.step) return
    byName[s.step] = { ...(byName[s.step] || { step: s.step }), status: s.status || 'pending', reason: s.reason || null, at: s.at || null }
  })
  return ISSUE_STEPS.map((step) => byName[step] || { step, status: 'pending', reason: null, at: null })
}

/**
 * Reduce one progress event (`progress` | `end` | `error`, the SSE names; a
 * plain JSON poll is a `progress` event) into the next ladder state. Pure.
 */
export function reduceProgress(prev, event) {
  const state = prev || initialProgress()
  if (!event) return state
  if (event.type === 'end') return { ...state, ended: true, terminal: true }
  if (event.type === 'error') return { ...state, error: event.error || 'stream failed' }
  if (event.type === 'transport') return { ...state, transport: event.transport || null }
  const d = event.data || {}
  const steps = mergeSteps(state.steps, d.steps)
  const running = steps.find((s) => s.status === 'running')
  const failed = d.failed_step || steps.find((s) => s.status === 'failed')?.step || null
  const terminal = d.terminal === true || ['delivered', 'failed', 'revoked'].includes(d.state)
  return {
    ...state,
    registrationId: d.registration_id || d.id || state.registrationId,
    state: d.state || state.state,
    terminal,
    failedStep: terminal && d.state === 'failed' ? failed : (failed || null),
    lastError: d.last_error || (d.last_error === null ? null : state.lastError),
    steps: terminal && d.state === 'delivered' ? steps.map((s) => (s.status === 'pending' && s.step === 'done' ? { ...s, status: 'done' } : s)) : steps,
    currentStep: running ? running.step : state.currentStep,
    dns: d.dns || state.dns,
    certificate: d.certificate || state.certificate,
    error: null
  }
}

// ── "Requesting…" must always end ───────────────────────────────────────────

/** A registration still working after this long is reported as slow, not silent. */
export const SLOW_AFTER_MS = 15 * 60 * 1000

export const SLOW_NOTE = 'Taking longer than expected'

export const NO_STEP_REASON = 'The platform has not reported a reason yet.'

/** The most recent thing the platform said about a ladder, or null. */
export function lastStepReason(progress) {
  const steps = (progress && progress.steps) || []
  const failed = steps.find((s) => s.status === 'failed')
  if (failed && failed.reason) return failed.reason
  const withReason = steps.filter((s) => s.reason)
  if (withReason.length) return withReason[withReason.length - 1].reason
  return (progress && progress.lastError) || null
}

/**
 * One honest line for a registration, whatever it is doing — the guard against
 * a row that says "Requesting…" for ever. Pure, so every state is testable.
 *
 * @param progress  a `reduceProgress` ladder
 * @param startedAt epoch ms the request was made (for the slow threshold)
 * @returns {{state, tone, label, detail, slow, terminal, settled, failed,
 *            failedStep, canRetry, currentStep}}
 */
export function progressStatus(progress, { now = Date.now(), startedAt = null, slowAfterMs = SLOW_AFTER_MS } = {}) {
  const p = progress || initialProgress()
  const steps = p.steps || []
  const running = steps.find((s) => s.status === 'running') || null
  const failedStep = p.failedStep || (steps.find((s) => s.status === 'failed') || {}).step || null
  const state = p.state || null
  const terminal = !!p.terminal || ['delivered', 'failed', 'revoked'].includes(state)
  const reason = lastStepReason(p)
  const base = {
    state,
    slow: false,
    terminal,
    settled: terminal,
    failed: false,
    failedStep,
    canRetry: false,
    currentStep: running ? running.step : (p.currentStep || null)
  }

  if (state === 'delivered') {
    return { ...base, tone: 'success', label: 'Issued', detail: 'The certificate was delivered to this environment.' }
  }
  if (state === 'failed') {
    return {
      ...base,
      tone: 'danger',
      failed: true,
      canRetry: true,
      label: failedStep ? `Failed at ${(STEP_COPY[failedStep] || { label: failedStep }).label.toLowerCase()}` : 'Request failed',
      detail: reason || NO_STEP_REASON
    }
  }
  if (state === 'revoked') {
    return { ...base, tone: 'neutral', label: 'Revoked', detail: reason || 'This registration was revoked.' }
  }

  const elapsed = typeof startedAt === 'number' && startedAt > 0 ? now - startedAt : 0
  const stepLabel = running ? (STEP_COPY[running.step] || { label: running.step }).label : null
  if (elapsed >= slowAfterMs) {
    return {
      ...base,
      tone: 'warning',
      slow: true,
      canRetry: true,
      label: SLOW_NOTE,
      detail: reason || (stepLabel ? `Still on “${stepLabel}”.` : NO_STEP_REASON)
    }
  }
  return {
    ...base,
    tone: 'info',
    label: stepLabel ? `${stepLabel}…` : (state ? String(state).replace(/_/g, ' ') : 'Requesting…'),
    detail: reason || (running ? (STEP_COPY[running.step] || {}).hint || null : null)
  }
}

/** The per-hostname badge for a name covered by a tracked registration. */
export function hostnameStatusFor(hostname, entries, opts = {}) {
  const name = str(hostname).toLowerCase()
  if (!name) return null
  const hit = (Array.isArray(entries) ? entries : []).find(
    (e) => e && (e.hostnames || []).map((h) => str(h).toLowerCase()).includes(name)
  )
  if (!hit) return null
  const status = progressStatus(hit.progress, { ...opts, startedAt: hit.startedAt })
  return { ...status, registrationId: hit.registrationId || (hit.progress && hit.progress.registrationId) || null }
}

/** Parse complete SSE frames out of a text buffer; returns `{ events, rest }`. */
export function parseSseBuffer(buffer) {
  const events = []
  const parts = String(buffer || '').split(/\n\n/)
  const rest = parts.pop() || ''
  parts.forEach((frame) => {
    let event = 'message'
    const dataLines = []
    frame.split('\n').forEach((line) => {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
    })
    if (!dataLines.length && event === 'message') return
    let data = null
    const raw = dataLines.join('\n')
    if (raw) { try { data = JSON.parse(raw) } catch (_) { data = raw } }
    events.push({ event, data })
  })
  return { events, rest }
}

/**
 * Follow a registration's progress: SSE over `fetch` (EventSource cannot
 * carry the bearer) with polling fallback — the same protocol as the operator
 * console. `onEvent` receives `{type: 'progress'|'end'|'error'|'transport', …}`.
 * Resolves when the stream ends or the poll reaches a terminal state.
 *
 *   fetchImpl(url, init)  — window.fetch-compatible
 *   poll()                — returns the JSON progress document
 */
export async function followProgress({
  url, headers = {}, fetchImpl, poll, onEvent, pollMs = 2000, maxPolls = 300, signal = null, sleep = null
} = {}) {
  const emit = (ev) => { try { onEvent && onEvent(ev) } catch (_) { /* never break the loop */ } }
  const wait = sleep || ((ms) => new Promise((resolve) => setTimeout(resolve, ms)))
  let streamed = false
  if (typeof fetchImpl === 'function') {
    try {
      const resp = await fetchImpl(url, { headers: { ...headers, Accept: 'text/event-stream' }, credentials: 'include', signal })
      const ctype = (resp.headers && typeof resp.headers.get === 'function' && resp.headers.get('content-type')) || ''
      if (resp.ok && /text\/event-stream/.test(ctype) && resp.body && typeof resp.body.getReader === 'function') {
        streamed = true
        emit({ type: 'transport', transport: 'sse' })
        const reader = resp.body.getReader()
        const decoder = new TextDecoder()
        let buf = ''
        let ended = false
        while (!ended) {
          const { value, done } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          const { events, rest } = parseSseBuffer(buf)
          buf = rest
          for (const ev of events) {
            if (ev.event === 'progress') emit({ type: 'progress', data: ev.data })
            else if (ev.event === 'end') { emit({ type: 'end' }); ended = true; break } else if (ev.event === 'error') emit({ type: 'error', error: ev.data && ev.data.message ? ev.data.message : String(ev.data || 'stream error') })
          }
        }
        if (ended) return 'sse'
        emit({ type: 'error', error: 'stream closed before the end event' })
      } else if (resp.status === 401 || resp.status === 403 || resp.status === 404) {
        emit({ type: 'error', error: `progress-http-${resp.status}` })
        return 'refused'
      }
    } catch (err) {
      if (signal && signal.aborted) return 'aborted'
      emit({ type: 'error', error: err && err.message ? err.message : 'stream failed' })
    }
  }
  if (typeof poll !== 'function') return streamed ? 'sse' : 'none'
  emit({ type: 'transport', transport: 'poll' })
  for (let i = 0; i < maxPolls; i += 1) {
    if (signal && signal.aborted) return 'aborted'
    let doc
    try {
      doc = await poll()
    } catch (err) {
      if (isForbidden(err) || (err && err.response && err.response.status === 404)) {
        emit({ type: 'error', error: `progress-http-${err.response.status}` })
        return 'refused'
      }
      emit({ type: 'error', error: err && err.message ? err.message : 'poll failed' })
      await wait(pollMs)
      continue
    }
    emit({ type: 'progress', data: doc })
    if (doc && (doc.terminal === true || ['delivered', 'failed', 'revoked'].includes(doc.state))) {
      emit({ type: 'end' })
      return 'poll'
    }
    await wait(pollMs)
  }
  return 'poll'
}

// ── the client ──────────────────────────────────────────────────────────────

function envelopeFrom(errOrResponse) {
  const resp = errOrResponse && errOrResponse.response ? errOrResponse.response : errOrResponse
  const body = resp && resp.data && typeof resp.data === 'object' ? resp.data : {}
  return { status: resp ? resp.status : undefined, body }
}

/**
 * Build the Issue-view client over an axios-like `http` (`get/post/put`
 * returning `{ status, data }`, rejecting with `.response` on 4xx/5xx).
 */
export function createCertIssueApi(http, { unwrap = unwrapEnvelope } = {}) {
  if (!http) throw new Error('createCertIssueApi: http client is required')
  return {
    /**
     * `PUT /registrations/ensure` → `{ ok, status, action, state,
     * registrationId, registration, message, errors, data }`. Never throws for
     * a business outcome (422 failed-at-validate / 504 budget / 403 carry the
     * registration when a row exists) — a transport failure still rejects.
     */
    async ensure(body) {
      let status, env
      try {
        const response = await http.put(`${BASE}/ensure`, body)
        status = response?.status
        env = response?.data || {}
      } catch (err) {
        if (!err || !err.response) throw err
        ;({ status, body: env } = envelopeFrom(err))
      }
      const data = (env && typeof env === 'object' && env.data && typeof env.data === 'object') ? env.data : {}
      const reg = data.registration || null
      return {
        ok: env && env.success !== false && status >= 200 && status < 300,
        status,
        action: data.action || null,
        state: data.state || (reg && reg.state) || null,
        registrationId: data.registration_id || (reg && reg.id) || null,
        registration: reg,
        message: (env && env.message) || null,
        errors: Array.isArray(env && env.errors) ? env.errors : [],
        // machine code of a refusal (e.g. 422 `certificate_policy_byo_only`, A-26) + its details
        code: (env && (env.error_code || (typeof env.error === 'string' ? env.error : null) || env.code)) ||
          (typeof data.error === 'string' ? data.error : null) || null,
        details: (env && env.details && typeof env.details === 'object') ? env.details : null,
        data
      }
    },

    /** `GET /registrations/<id>/progress[?since=]` → the JSON progress document. */
    async progress(id, { since = null } = {}) {
      const params = since ? { since } : {}
      const response = await http.get(`${BASE}/${enc(id)}/progress`, { params })
      return unwrap(response)
    },

    /** `POST /registrations/<id>/generate` (Retry — resumes at the failed step). */
    async generate(id, { issuerProfile = null, wait = true, budgetS = 240 } = {}) {
      const body = { wait: !!wait, budget_s: budgetS }
      if (issuerProfile) body.issuer_profile = issuerProfile
      const response = await http.post(`${BASE}/${enc(id)}/generate`, body)
      return unwrap(response)
    },

    /** `POST /registrations/<id>/upload` (BYO PEM, A-18). */
    async upload(id, body) {
      const response = await http.post(`${BASE}/${enc(id)}/upload`, body)
      return unwrap(response)
    },

    /**
     * `POST /registrations/<id>/bind-ingresses` — point every Ingress in the
     * registration's namespace that serves its hostnames at the delivered
     * Secret. Idempotent; the route always answers 200 with the outcome as
     * the payload, so the only throw here is a transport failure or a 403
     * (the grant was revoked mid-session).
     */
    async bindIngresses(id) {
      const response = await http.post(`${BASE}/${enc(id)}/bind-ingresses`, {})
      return unwrap(response)
    },

    /** `POST /registrations/<id>/validate` (step 3 on demand; never orders). */
    async validate(id) {
      const response = await http.post(`${BASE}/${enc(id)}/validate`, {})
      return unwrap(response)
    },

    /** `GET /registrations?org_id=&cluster=&state=` → `{ registrations, count }`. */
    async list(params = {}) {
      const clean = {}
      Object.entries(params || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') clean[k] = v })
      const response = await http.get(BASE, { params: clean })
      return unwrap(response) || { registrations: [], count: 0 }
    },

    /** Progress URL for the SSE fetch (relative to the http base). */
    progressUrl(id, baseUrl = '') {
      return `${baseUrl || ''}${BASE}/${enc(id)}/progress`
    }
  }
}
